/*
 * Feature usage from the browser: track('chart.draw', { tool: 'fibonacciLine' }).
 *
 * Two destinations:
 *   - our own log (POST /usage/track -> feature_events, shown at /admin/usage). First-party,
 *     user id only, so it is NOT gated on the cookie banner. Events are queued and sent in
 *     one batch every few seconds and when the tab is hidden (keepalive, so a click right
 *     before navigating away still arrives).
 *   - Google Analytics, only when the visitor accepted the banner (consent.js).
 *
 * Names are lowercase 'area.action' (see NAME_RE in app/usage.py). Props are a few short
 * scalars; never put an id, a price or anything typed by the user in them.
 *
 * Markup can report a click without any script: <button data-track="scanner.open">.
 * Failures are swallowed: measuring must never break the page.
 */
const FLUSH_MS = 5000;
const MAX_QUEUE = 50;
let queue = [];
let timer = null;

function csrf() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') || '' : '';
}

function flush() {
  if (timer) { clearTimeout(timer); timer = null; }
  if (!queue.length) return;
  const events = queue.splice(0, 20);
  try {
    fetch('/usage/track', {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf() },
      body: JSON.stringify({ events }),
    }).catch(() => {});
  } catch (e) { /* ignore */ }
  if (queue.length) timer = setTimeout(flush, FLUSH_MS);
}

export function track(name, props) {
  try {
    if (queue.length < MAX_QUEUE) {
      queue.push({ name, path: location.pathname, props: props || undefined });
      if (!timer) timer = setTimeout(flush, FLUSH_MS);
    }
    const consent = window.indicalertConsent;
    if (window.gtag && consent && consent.granted && consent.granted()) {
      window.gtag('event', name.replace(/\./g, '_'), props || {});
    }
  } catch (e) { /* ignore */ }
}

/* Wrap a function that the templates call by bare name so each call is counted. */
export function tracked(name, fn, propsOf) {
  return function (...args) {
    track(name, propsOf ? propsOf(...args) : undefined);
    return fn.apply(this, args);
  };
}

/* Screen class from the viewport width, so mobile and desktop can be told apart. No user
   agent string is read or stored. */
function deviceClass() {
  const w = window.innerWidth || 0;
  return w < 768 ? 'mobile' : w < 1100 ? 'tablet' : 'desktop';
}

export function initTracking() {
  window.track = track;
  track('page.view', { device: deviceClass() });
  document.addEventListener('click', (e) => {
    const el = e.target.closest && e.target.closest('[data-track]');
    if (el) track(el.getAttribute('data-track'));
  }, true);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', flush);
}
